import React, { useState, useMemo } from 'react';
import { useT } from '../i18n.js';
import { downloadCsv } from '../api.js';
import { CATEGORIES, COMPLIANCE_META, SUPPLIERS, catOf, fmtTHB, netPayableOf } from './data.jsx';
import { IconArrow, IconBank, IconBuilding, IconCal, IconChart, IconCheck, IconChevron, IconChevronDn, IconClock, IconDots, IconDownload, IconExt, IconEye, IconFile, IconFilter, IconPlus, IconReceipt, IconSearch, IconShield, IconUpload, IconUsers, IconX } from './icons.jsx';
import { Avatar, CategoryTag, RatingBar, StatusPill } from './screens-internal.jsx';

import { useState as useStateAdm, useMemo as useMemoAdm } from 'react';

/* ============================================================
   SHARED — tiny inline SVG charts
   ============================================================ */
function BarChart({ data, height = 140, accentIndex = -1 }) {
  const max = Math.max(...data.map(d => d.v));
  const W = 480, H = height, pad = 24;
  const bw = (W - pad * 2) / data.length;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{display:"block"}}>
      {[0, 1, 2, 3].map(i => {
        const y = pad + ((H - pad * 2) / 3) * i;
        return <line key={i} x1={pad} x2={W - pad} y1={y} y2={y} stroke="var(--border-subtle)" strokeWidth="1"/>;
      })}
      {data.map((d, i) => {
        const h = ((H - pad * 2) * d.v) / max;
        const x = pad + bw * i + 4;
        const y = H - pad - h;
        const w = bw - 8;
        const isAccent = i === accentIndex;
        return (
          <g key={i}>
            <rect x={x} y={y} width={w} height={h} rx="3"
              fill={isAccent ? "var(--accent)" : "var(--border-default)"}/>
            <text x={x + w/2} y={H - pad + 14} fontSize="10" textAnchor="middle"
              fill="var(--text-tertiary)" fontFamily="var(--font-mono)">{d.l}</text>
          </g>
        );
      })}
    </svg>
  );
}

function AreaChart({ data, height = 160, color = "var(--accent)" }) {
  const max = Math.max(...data.map(d => d.v)) * 1.1;
  const W = 720, H = height, pad = 28;
  const innerW = W - pad * 2;
  const innerH = H - pad * 2;
  const pts = data.map((d, i) => {
    const x = pad + (innerW * i) / (data.length - 1);
    const y = pad + innerH - (innerH * d.v) / max;
    return [x, y];
  });
  const path = "M " + pts.map(p => p.join(",")).join(" L ");
  const fill = path + ` L ${pts[pts.length-1][0]},${pad + innerH} L ${pts[0][0]},${pad + innerH} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{display:"block"}}>
      <defs>
        <linearGradient id="ac-grad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor={color} stopOpacity="0.3"/>
          <stop offset="1" stopColor={color} stopOpacity="0"/>
        </linearGradient>
      </defs>
      {[0, 1, 2, 3, 4].map(i => {
        const y = pad + (innerH / 4) * i;
        return <line key={i} x1={pad} x2={W - pad} y1={y} y2={y} stroke="var(--border-subtle)" strokeWidth="1"/>;
      })}
      <path d={fill} fill="url(#ac-grad)"/>
      <path d={path} fill="none" stroke={color} strokeWidth="2"/>
      {pts.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r="2.5" fill={color}/>
      ))}
      {data.map((d, i) => (
        <text key={i} x={pts[i][0]} y={H - 8} fontSize="10" textAnchor="middle"
          fill="var(--text-tertiary)" fontFamily="var(--font-mono)">{d.l}</text>
      ))}
    </svg>
  );
}

function DonutChart({ data, size = 180 }) {
  const total = data.reduce((a, b) => a + b.v, 0);
  const r = size / 2 - 14;
  const cx = size / 2, cy = size / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} style={{display:"block"}}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--border-subtle)" strokeWidth="20"/>
      {data.map((d, i) => {
        const len = (d.v / total) * c;
        const el = (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none"
            stroke={d.color} strokeWidth="20"
            strokeDasharray={`${len} ${c - len}`}
            strokeDashoffset={-offset}
            transform={`rotate(-90 ${cx} ${cy})`}
            strokeLinecap="butt"/>
        );
        offset += len;
        return el;
      })}
      <text x={cx} y={cy - 4} textAnchor="middle" fontSize="22" fontWeight="600"
        fill="var(--text-primary)" fontFamily="var(--font-en)">
        {fmtTHB(total).replace("฿", "฿")}
      </text>
      <text x={cx} y={cy + 14} textAnchor="middle" fontSize="10"
        fill="var(--text-tertiary)" fontFamily="var(--font-mono)" letterSpacing="0.08em">
        TOTAL SPEND
      </text>
    </svg>
  );
}

function MiniSpark({ data, color = "var(--accent)", width = 80, height = 24 }) {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * height;
    return `${x},${y.toFixed(1)}`;
  }).join(" ");
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{display:"block"}}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5"/>
    </svg>
  );
}

/* ============================================================
   SCREEN — COMPLIANCE
   ============================================================ */
const COMPLIANCE_ROWS = SUPPLIERS.map((s, i) => {
  // Synthesise document expiry data per supplier
  const seed = i + 1;
  const expiresIn = (seed * 37) % 360 - 30; // -30 to 330 days
  return {
    ...s,
    docs: {
      bizReg:    { state: "verified", expires: ((seed * 19) % 600) + 60 },
      vat:       { state: i === 3 ? "expired" : (i === 11 ? "expiring" : "verified"), expires: i === 3 ? -8 : (i === 11 ? 12 : 220) },
      bank:      { state: i === 12 ? "missing" : "verified", expires: 480 },
      insurance: { state: i % 5 === 0 ? "expiring" : "verified", expires: i % 5 === 0 ? 22 : 180 },
      msa:       { state: "verified", expires: 730 },
    },
    kycLast: ["3 days ago", "2 weeks ago", "1 month ago", "3 months ago", "5 months ago", "9 months ago"][i % 6],
    nextReview: i % 4 === 0 ? "Overdue" : ((i * 13) % 90 + 15) + " days",
    riskScore: ((i * 11) % 100),
  };
});

// ⚠ MODIFIED FROM PORT — accepts live compliance + vendors data.
// Detail table still uses prototype mock until Phase 4 polish.
export function ScreenCompliance({ compliance = [], vendors = [] }) {
  const liveValid    = compliance.filter(c => c.status === 'valid').length;
  const liveExpiring = compliance.filter(c => c.status === 'expiring-30d').length;
  const liveExpired  = compliance.filter(c => c.status === 'expired').length;
  const liveMissing  = compliance.filter(c => c.status === 'missing').length;
  const blacklistCount = vendors.filter(v => v.status === 'blacklist').length;
  const [tab, setTab] = useStateAdm("all");

  const filtered = useMemoAdm(() => {
    if (tab === "expiring") return COMPLIANCE_ROWS.filter(r =>
      Object.values(r.docs).some(d => d.state === "expiring" || d.state === "expired"));
    if (tab === "kyc") return COMPLIANCE_ROWS.filter(r => r.compliance === "kyc-pending");
    if (tab === "black") return COMPLIANCE_ROWS.filter(r => r.compliance === "rejected" || r.status === "blacklist");
    return COMPLIANCE_ROWS;
  }, [tab]);

  const docCell = (d) => {
    const map = {
      verified:  { icon: <IconCheck size={11}/>, color: "var(--success)" },
      expiring:  { icon: "!", color: "var(--warning)" },
      expired:   { icon: <IconX size={11}/>, color: "var(--danger)" },
      missing:   { icon: "?", color: "var(--text-tertiary)" },
    };
    const m = map[d.state] || map.missing;
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        fontSize: 11, color: m.color, fontFamily: "var(--font-mono)",
      }}>
        <span style={{
          width: 14, height: 14, borderRadius: "50%",
          background: `color-mix(in oklab, ${m.color} 18%, transparent)`,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          fontWeight: 600, fontSize: 10,
        }}>{m.icon}</span>
        {d.state === "verified" ? "OK" :
         d.state === "expiring" ? `${d.expires}d` :
         d.state === "expired" ? "EXP" :
         "—"}
      </span>
    );
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title-group">
          <h1>Compliance</h1>
          <div className="sub">KYC, document expiry &amp; blacklist monitoring · last audit run 4 hours ago</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary btn-sm"><IconDownload size={14}/> Export audit pack</button>
          <button className="btn btn-primary btn-sm"><IconShield size={14}/> Run re-verification</button>
        </div>
      </div>

      <div className="stat-row">
        <div className="stat-tile">
          <div className="label">Valid documents</div>
          <div className="val tnum" style={{color: "var(--success)"}}>{liveValid}</div>
          <div className="delta">across {vendors.length} vendors</div>
        </div>
        <div className="stat-tile">
          <div className="label">Expiring (30d)</div>
          <div className="val tnum" style={{color: "var(--warning)"}}>{liveExpiring}</div>
          <div className="delta">Need re-verification soon</div>
        </div>
        <div className="stat-tile">
          <div className="label">Expired</div>
          <div className="val tnum" style={{color: "var(--danger)"}}>{liveExpired}</div>
          <div className="delta">Block billing until renewed</div>
        </div>
        <div className="stat-tile">
          <div className="label">Blacklisted vendors</div>
          <div className="val tnum" style={{color: "var(--danger)"}}>{blacklistCount}</div>
          <div className="delta down">Cannot transact</div>
        </div>
      </div>

      <div className="tabs">
        {[
          ["all",      "All vendors",     COMPLIANCE_ROWS.length],
          ["expiring", "Docs expiring",   8],
          ["kyc",      "KYC pending",     3],
          ["black",    "Blacklist",       1],
          ["history",  "Re-verification log", null],
        ].map(([id, lbl, n]) => (
          <button key={id} className={"tab " + (tab === id ? "active" : "")} onClick={() => setTab(id)}>
            {lbl} {n !== null && <span className="muted" style={{marginLeft: 6, fontSize: 11}}>{n}</span>}
          </button>
        ))}
      </div>

      <div className="toolbar">
        <button className="chip"><IconFilter size={12}/> Filters</button>
        <button className="chip">Category</button>
        <button className="chip">Risk score</button>
        <button className="chip">Owner</button>
        <button className="chip">Last reviewed</button>
        <div className="toolbar-right">
          <div className="topbar-search" style={{maxWidth: 220, margin: 0}}>
            <IconSearch size={14} className="icon"/>
            <input placeholder="Search vendors…"/>
          </div>
          <button className="chip"><IconCal size={12}/> Last 30 days</button>
        </div>
      </div>

      <div className="tbl-wrap">
        <table className="tbl">
          <colgroup>
            <col style={{width: 260}}/>
            <col style={{width: 130}}/>
            <col style={{width: 110}}/>
            <col style={{width: 80}}/>
            <col style={{width: 80}}/>
            <col style={{width: 80}}/>
            <col style={{width: 90}}/>
            <col style={{width: 80}}/>
            <col style={{width: 120}}/>
            <col style={{width: 120}}/>
            <col style={{width: 90}}/>
          </colgroup>
          <thead>
            <tr>
              <th>Vendor</th>
              <th>Category</th>
              <th>Status</th>
              <th>Biz reg</th>
              <th>VAT cert</th>
              <th>Bank ltr</th>
              <th>Insurance</th>
              <th>MSA</th>
              <th>Last KYC</th>
              <th>Next review</th>
              <th className="right">Risk</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.id}>
                <td>
                  <div style={{display:"flex", alignItems:"center", gap:10}}>
                    <Avatar name={r.name} size="sm"/>
                    <div style={{minWidth:0}}>
                      <div className="ellipsis" style={{fontSize: 13}}>{r.name}</div>
                      <div className="mono muted" style={{fontSize: 11}}>{r.id}</div>
                    </div>
                  </div>
                </td>
                <td><CategoryTag id={r.cat}/></td>
                <td><StatusPill meta={COMPLIANCE_META[r.compliance]}/></td>
                <td>{docCell(r.docs.bizReg)}</td>
                <td>{docCell(r.docs.vat)}</td>
                <td>{docCell(r.docs.bank)}</td>
                <td>{docCell(r.docs.insurance)}</td>
                <td>{docCell(r.docs.msa)}</td>
                <td className="muted" style={{fontSize: 12}}>{r.kycLast}</td>
                <td>
                  <span className="mono" style={{
                    fontSize: 12,
                    color: r.nextReview === "Overdue" ? "var(--danger)" : "var(--text-secondary)"
                  }}>{r.nextReview}</span>
                </td>
                <td className="right">
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    fontFamily: "var(--font-mono)", fontSize: 12,
                    color: r.riskScore > 70 ? "var(--danger)" : r.riskScore > 40 ? "var(--warning)" : "var(--success)",
                  }}>
                    <span style={{
                      width: 30, height: 4, background: "var(--interactive-secondary)",
                      borderRadius: 2, position: "relative", overflow: "hidden",
                    }}>
                      <span style={{
                        position: "absolute", left: 0, top: 0, bottom: 0,
                        width: r.riskScore + "%",
                        background: r.riskScore > 70 ? "var(--danger)" : r.riskScore > 40 ? "var(--warning)" : "var(--success)",
                      }}></span>
                    </span>
                    {r.riskScore}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="status-bar">
        <span>{filtered.length} of {COMPLIANCE_ROWS.length} vendors</span>
        <span>·</span>
        <span>Next batch re-verification scheduled May 24</span>
        <span style={{marginLeft:"auto"}}>Last sync: 4h ago · PDPA-compliant</span>
      </div>
    </div>
  );
}

/* ============================================================
   SCREEN — AUDIT LOG
   ============================================================ */
const AUDIT_ACTIONS = [
  { id: "approve",  label: "Approved",        color: "var(--success)", icon: <IconCheck size={11}/> },
  { id: "reject",   label: "Rejected",        color: "var(--danger)",  icon: <IconX size={11}/> },
  { id: "create",   label: "Created",         color: "var(--accent)",  icon: <IconPlus size={11}/> },
  { id: "edit",     label: "Edited",          color: "var(--info)",    icon: "✎" },
  { id: "view",     label: "Viewed",          color: "var(--text-tertiary)", icon: <IconEye size={11}/> },
  { id: "upload",   label: "Uploaded",        color: "var(--info)",    icon: <IconUpload size={11}/> },
  { id: "login",    label: "Signed in",       color: "var(--text-tertiary)", icon: "→" },
];

const AUDIT_EVENTS = [
  { t: "10:42:18", date: "Today",      actor: "Ananda V.",       role: "Finance",    action: "approve", entity: "INV-2026-0418", target: "Tinker Post Bangkok", note: "Verified bank match. Approved for payment.", ip: "203.150.18.42", before: "Finance verify", after: "Approved for payment" },
  { t: "10:31:04", date: "Today",      actor: "Ploy V.",         role: "PM",         action: "approve", entity: "INV-2026-0418", target: "Tinker Post Bangkok", note: "PO matches. Final mix delivered on time.", ip: "203.150.18.11", before: "PM review", after: "Finance verify" },
  { t: "09:58:22", date: "Today",      actor: "system",          role: "OCR",        action: "create",  entity: "INV-2026-0518", target: "Tinker Post Bangkok", note: "Invoice received via portal. OCR confidence 98.4%.", ip: "—", before: null, after: "PM review" },
  { t: "09:48:11", date: "Today",      actor: "Tinker Post BKK", role: "Vendor",     action: "upload",  entity: "INV-2026-0518.pdf", target: "Portal", note: "1.8 MB · 3 line items detected", ip: "171.96.224.18", before: null, after: null },
  { t: "08:42:00", date: "Today",      actor: "Ploy V.",         role: "PM",         action: "login",   entity: "Vendor OS", target: "—", note: "Web · Chrome 124 · BKK", ip: "203.150.18.11", before: null, after: null },
  { t: "17:14:08", date: "Yesterday",  actor: "Tee S.",          role: "PM",         action: "create",  entity: "VND-1313",      target: "Citylight Banners Ltd.", note: "New supplier registration submitted to Procurement queue.", ip: "203.150.18.55", before: null, after: "Pending approval" },
  { t: "16:51:24", date: "Yesterday",  actor: "Aim K.",          role: "Procurement",action: "edit",    entity: "VND-1042",      target: "Tinker Post Bangkok", note: "Credit terms changed.", ip: "203.150.18.78", before: "NET 30",  after: "NET 45" },
  { t: "15:02:18", date: "Yesterday",  actor: "Win C.",          role: "AE",         action: "view",    entity: "VND-1248",      target: "Bright Lens Productions", note: "Profile viewed.", ip: "203.150.18.40", before: null, after: null },
  { t: "11:18:44", date: "Yesterday",  actor: "Ananda V.",       role: "Finance",    action: "reject",  entity: "INV-2026-0501", target: "Krungsri auto loan", note: "PO line items don't reconcile. Returned to vendor with comments.", ip: "203.150.18.42", before: "Finance verify", after: "Rejected" },
  { t: "09:11:00", date: "May 14",     actor: "Aim K.",          role: "Procurement",action: "approve", entity: "VND-1314",      target: "Reverb Sound Stage", note: "All KYC checks cleared. Vendor active.", ip: "203.150.18.78", before: "Pending approval", after: "Active" },
  { t: "16:24:31", date: "May 13",     actor: "system",          role: "Cron",       action: "edit",    entity: "VND-1212",      target: "Drayage Express", note: "Auto-flagged on quality threshold (score 2.4).", ip: "—", before: "Active", after: "Under review" },
  { t: "14:08:12", date: "May 13",     actor: "Mod K.",          role: "BD",         action: "edit",    entity: "VND-1051",      target: "Aurora Media Group", note: "Bank account updated.", ip: "203.150.18.33", before: "xxx-x-x3491-x", after: "xxx-x-x4781-x" },
  { t: "11:52:48", date: "May 13",     actor: "Earth P.",        role: "PM",         action: "upload",  entity: "msa-signed.pdf", target: "Tinker Post Bangkok", note: "MSA 2026 countersigned.", ip: "203.150.18.91", before: null, after: null },
];

// ⚠ MODIFIED FROM PORT — accepts live audits from the backend Audit sheet.
export function ScreenAudit({ audits }) {
  const t = useT();
  const source = audits && audits.length ? audits : (audits ? [] : AUDIT_EVENTS);
  const [openIdx, setOpenIdx] = useStateAdm(0);
  const [actionFilter, setActionFilter] = useStateAdm(new Set());

  const filtered = source.filter(e =>
    actionFilter.size === 0 || actionFilter.has(e.action)
  );

  const groups = filtered.reduce((acc, e) => {
    (acc[e.date] = acc[e.date] || []).push(e);
    return acc;
  }, {});

  const open = filtered[openIdx] || filtered[0];

  const toggleAction = (id) => {
    const n = new Set(actionFilter);
    n.has(id) ? n.delete(id) : n.add(id);
    setActionFilter(n);
  };

  const actionMeta = (id) => AUDIT_ACTIONS.find(a => a.id === id) || AUDIT_ACTIONS[0];

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title-group">
          <h1>{t("Audit log")}</h1>
          <div className="sub">{t("Every action across vendors, invoices and accounts · retained 7 years")}</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary btn-sm" disabled={!filtered.length}
            onClick={() => downloadCsv(`audit-${new Date().toISOString().slice(0,10)}.csv`, filtered, [
              { label: 'Date', key: 'date' },
              { label: 'Time', key: 't' },
              { label: 'Actor', key: 'actor' },
              { label: 'Role', key: 'role' },
              { label: 'Action', get: e => e.rawAction || e.action },
              { label: 'Entity', key: 'entity' },
              { label: 'Type', key: 'target' },
            ])}>
            <IconDownload size={14}/> {t('Export CSV')}
          </button>
        </div>
      </div>

      <div className="toolbar">
        <span className="muted" style={{fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em"}}>{t('Action')}</span>
        {AUDIT_ACTIONS.map(a => (
          <button key={a.id}
            className={"chip " + (actionFilter.has(a.id) ? "active" : "")}
            onClick={() => toggleAction(a.id)}>
            <span style={{
              width: 12, height: 12, borderRadius: "50%",
              background: `color-mix(in oklab, ${a.color} 22%, transparent)`,
              color: a.color, display: "inline-flex", alignItems: "center", justifyContent: "center",
              fontSize: 9,
            }}>{a.icon}</span>
            {t(a.label)}
          </button>
        ))}
      </div>

      <div style={{display: "grid", gridTemplateColumns: "1fr 420px", flex: 1, overflow: "hidden"}}>
        <div style={{overflowY: "auto", borderRight: "1px solid var(--border-subtle)"}}>
          {Object.entries(groups).map(([date, events]) => (
            <div key={date}>
              <div style={{
                position: "sticky", top: 0, zIndex: 2,
                background: "var(--bg-primary)",
                padding: "10px 24px 8px",
                fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em",
                color: "var(--text-tertiary)",
                borderBottom: "1px solid var(--border-subtle)",
                fontFamily: "var(--font-mono)",
              }}>
                {date} <span style={{marginLeft: 8}}>{t('{n} events', { n: events.length })}</span>
              </div>
              {events.map((e, i) => {
                const idx = filtered.indexOf(e);
                const a = actionMeta(e.action);
                return (
                  <div key={i}
                    onClick={() => setOpenIdx(idx)}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "70px 22px 1fr auto",
                      gap: 12,
                      alignItems: "center",
                      padding: "10px 24px",
                      borderBottom: "1px solid var(--border-subtle)",
                      cursor: "pointer",
                      background: idx === openIdx ? "var(--accent-soft)" : "transparent",
                      borderLeft: idx === openIdx ? "3px solid var(--accent)" : "3px solid transparent",
                    }}>
                    <span className="mono muted" style={{fontSize: 11}}>{e.t}</span>
                    <span style={{
                      width: 22, height: 22, borderRadius: "50%",
                      background: `color-mix(in oklab, ${a.color} 22%, transparent)`,
                      color: a.color, display: "inline-flex", alignItems: "center", justifyContent: "center",
                    }}>{a.icon}</span>
                    <div style={{minWidth: 0, fontSize: 13, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap"}}>
                      <span style={{color: "var(--text-primary)"}}>{e.actor}</span>
                      <span className="muted">{t(a.label).toLowerCase()}</span>
                      <span className="mono" style={{fontSize: 12, color: "var(--text-secondary)"}}>{e.entity}</span>
                      <span className="muted">·</span>
                      <span className="muted ellipsis" style={{fontSize: 12, minWidth: 0}}>{e.target}</span>
                    </div>
                    <Avatar name={e.actor === "system" ? "SY" : e.actor} size="sm"/>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div style={{overflowY: "auto", padding: 20, background: "var(--bg-secondary)"}}>
          {open && (() => {
            const a = actionMeta(open.action);
            return (
              <div style={{display: "flex", flexDirection: "column", gap: 14}}>
                <div style={{display: "flex", alignItems: "center", gap: 12}}>
                  <span style={{
                    width: 40, height: 40, borderRadius: "50%",
                    background: `color-mix(in oklab, ${a.color} 22%, transparent)`,
                    color: a.color, display: "inline-flex", alignItems: "center", justifyContent: "center",
                  }}>{a.icon}</span>
                  <div>
                    <div style={{fontSize: 16, fontWeight: 500}}>{t(a.label)} <span className="mono">{open.entity}</span></div>
                    <div className="muted" style={{fontSize: 12}}>{open.date} · {open.t} · {open.target}</div>
                  </div>
                </div>

                <dl className="kv" style={{gridTemplateColumns: "100px 1fr"}}>
                  <dt>{t('Actor')}</dt>
                  <dd style={{display:"flex", alignItems:"center", gap: 8}}>
                    <Avatar name={open.actor === "system" ? "SY" : open.actor} size="sm"/>
                    {open.actor} <span className="muted">· {open.role}</span>
                  </dd>
                  <dt>{t('Action')}</dt><dd>{t(a.label)}</dd>
                  <dt>{t('Entity')}</dt><dd className="mono">{open.entity}</dd>
                  <dt>{t('Timestamp')}</dt><dd className="mono">{open.date}, {open.t} ICT</dd>
                  <dt>IP</dt><dd className="mono">{open.ip}</dd>
                  <dt>{t('Session')}</dt><dd className="mono">sess_8f2a91e3</dd>
                </dl>

                {(open.before || open.after) && (
                  <div>
                    <div style={{fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-tertiary)", marginBottom: 8}}>{t('State change')}</div>
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 24px 1fr",
                      gap: 8,
                      alignItems: "center",
                    }}>
                      <div style={{
                        padding: 10,
                        borderRadius: "var(--radius-md)",
                        border: "1px solid var(--border-subtle)",
                        fontFamily: "var(--font-mono)",
                        fontSize: 12,
                        color: "var(--text-tertiary)",
                        background: "var(--bg-primary)",
                        textDecoration: open.before ? "line-through" : "none",
                      }}>
                        {open.before || <span className="muted" style={{fontStyle: "italic", textDecoration: "none"}}>(none)</span>}
                      </div>
                      <div style={{textAlign: "center", color: "var(--text-tertiary)"}}><IconArrow size={14}/></div>
                      <div style={{
                        padding: 10,
                        borderRadius: "var(--radius-md)",
                        border: "1px solid var(--accent)",
                        background: "var(--accent-soft)",
                        fontFamily: "var(--font-mono)",
                        fontSize: 12,
                        color: "var(--text-primary)",
                      }}>
                        {open.after || <span className="muted" style={{fontStyle: "italic"}}>(none)</span>}
                      </div>
                    </div>
                  </div>
                )}

                {open.note && (
                  <div>
                    <div style={{fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-tertiary)", marginBottom: 8}}>{t('Note')}</div>
                    <div style={{
                      padding: 12, borderRadius: "var(--radius-md)",
                      background: "var(--bg-primary)",
                      border: "1px solid var(--border-subtle)",
                      fontSize: 13, lineHeight: 1.5, color: "var(--text-secondary)",
                    }}>{open.note}</div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   SCREEN — REPORTS
   ============================================================ */
// ⚠ MODIFIED FROM PORT — accepts live vendors + invoices and computes
// real metrics. Monthly trend chart still uses mock data for visual purposes
// (true monthly spend needs paid_at dates spread across months; with current
// data the trend would be flat).
export function ScreenReports({ vendors = [], invoices = [] }) {
  const t = useT();
  // ⚠ REWRITTEN (2026-05-31) — everything below computes from live vendors +
  // invoices. No mock numbers, no Math.random.
  // No mock fallback — show real vendors (empty until loaded), never SUPPLIERS.
  const vList = vendors || [];
  const invList = invoices || [];
  const amountOf = (i) => netPayableOf(i);
  const vidOf = (i) => i.vendor_id || i.vid;
  const vById = {}; vList.forEach(v => { vById[v.vendor_id || v.id] = v; });

  // Live category breakdown from real vendor spend
  const byCategory = CATEGORIES.map(c => {
    const s = vList.filter(s => s.cat === c.id).reduce((a,b) => a + (Number(b.spend) || 0), 0);
    return { l: c.label, v: s, color: c.color };
  }).filter(d => d.v > 0).sort((a,b) => b.v - a.v);

  const topVendors = [...vList].sort((a,b) => (Number(b.spend)||0) - (Number(a.spend)||0)).slice(0, 6);

  // Live headline stats
  const totalSpend  = vList.reduce((a, b) => a + (Number(b.spend) || 0), 0);
  const activeCount = vList.filter(v => v.status === 'active').length;
  const paidInv     = invList.filter(i => i.status === 'paid');
  const disputedInv = invList.filter(i => i.status === 'disputed' || i.status === 'rejected');
  const disputeRate = invList.length ? (disputedInv.length / invList.length * 100).toFixed(1) : '0.0';
  const daysToPay = paidInv.map(i => {
    if (!i.paid_at || !i.submitted_at) return null;
    return (new Date(i.paid_at) - new Date(i.submitted_at)) / 86400000;
  }).filter(x => x != null && x >= 0);
  const avgDays = daysToPay.length
    ? +(daysToPay.reduce((a,b) => a+b, 0) / daysToPay.length).toFixed(1)
    : 0;

  // --- Monthly spend: last 12 months from paid invoices' paid_at ---
  const now = new Date();
  const months = [];
  const monthIdx = {};
  for (let k = 11; k >= 0; k--) {
    const d = new Date(now.getFullYear(), now.getMonth() - k, 1);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    monthIdx[key] = months.length;
    months.push({ key, l: d.toLocaleDateString(undefined, { month: 'short' }), v: 0 });
  }
  paidInv.forEach(i => {
    if (!i.paid_at) return;
    const d = new Date(i.paid_at);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    if (key in monthIdx) months[monthIdx[key]].v += amountOf(i);
  });
  const monthly = months.map(m => ({ l: m.l, v: m.v }));
  const hasMonthly = monthly.some(m => m.v > 0);
  const thisMonthV = monthly[monthly.length - 1]?.v || 0;
  const priorV = monthly[monthly.length - 2]?.v || 0;
  const momPct = priorV > 0 ? Math.round((thisMonthV - priorV) / priorV * 100) : null;
  const peak = monthly.reduce((a, b) => (b.v > a.v ? b : a), { l: '—', v: 0 });
  const total12 = monthly.reduce((a, b) => a + b.v, 0);

  // Real per-vendor 12-month paid-spend sparkline
  const sparkFor = (vid) => {
    const arr = months.map(() => 0);
    paidInv.forEach(i => {
      if (vidOf(i) !== vid || !i.paid_at) return;
      const d = new Date(i.paid_at);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      if (key in monthIdx) arr[monthIdx[key]] += amountOf(i);
    });
    return arr;
  };

  // --- Payment cycle: avg days per real stage transition (paid invoices) ---
  const stageAvg = (fromF, toF) => {
    const vals = paidInv.map(i => {
      const a = i[fromF] ? new Date(i[fromF]).getTime() : null;
      const b = i[toF] ? new Date(i[toF]).getTime() : null;
      if (a == null || b == null || b < a) return null;
      return (b - a) / 86400000;
    }).filter(x => x != null);
    return vals.length ? +(vals.reduce((a,b)=>a+b,0) / vals.length).toFixed(1) : null;
  };
  const cycleStages = [
    { l: "PM",   v: stageAvg('submitted_at', 'pm_decision_at'),        name: "PM review" },
    { l: "Fin",  v: stageAvg('pm_decision_at', 'finance_decision_at'), name: "Finance" },
    { l: "Sched",v: stageAvg('finance_decision_at', 'payment_scheduled'), name: "Scheduling" },
    { l: "Pay",  v: stageAvg('payment_scheduled', 'paid_at'),          name: "Pay-out" },
  ].filter(s => s.v != null);
  const cycleBars = [...cycleStages.map(s => ({ l: s.l, v: s.v })), { l: "Total", v: avgDays }];
  const namedStages = cycleStages.filter(s => s.v != null);
  const fastest = namedStages.length ? namedStages.reduce((a,b)=> b.v < a.v ? b : a) : null;
  const slowest = namedStages.length ? namedStages.reduce((a,b)=> b.v > a.v ? b : a) : null;

  // --- Category performance from live vendors + invoices ---
  const catRows = CATEGORIES.map(c => {
    const vs = vList.filter(v => v.cat === c.id);
    if (!vs.length) return null;
    const vids = new Set(vs.map(v => v.vendor_id || v.id));
    const inv = invList.filter(i => vids.has(vidOf(i)));
    const spend = vs.reduce((a,b)=>a+(Number(b.spend)||0),0);
    const rated = vs.filter(v => Number(v.rating) > 0);
    const avgQ = rated.length ? (rated.reduce((a,b)=>a+Number(b.rating),0)/rated.length) : null;
    const paid = inv.filter(i => i.status === 'paid' && i.paid_at);
    const onT = paid.filter(i => !i.due_at || new Date(i.paid_at) <= new Date(i.due_at));
    const onTimePct = paid.length ? Math.round(onT.length/paid.length*100) : null;
    const cyc = paid.filter(i=>i.submitted_at).map(i => (new Date(i.paid_at)-new Date(i.submitted_at))/86400000).filter(x=>x>=0);
    const avgCyc = cyc.length ? +(cyc.reduce((a,b)=>a+b,0)/cyc.length).toFixed(1) : null;
    const disp = inv.filter(i => i.status === 'disputed' || i.status === 'rejected');
    const dispRate = inv.length ? +(disp.length/inv.length*100).toFixed(1) : null;
    return { c, vendors: vs.length, spend, avgQ, onTimePct, avgCyc, invoices: inv.length, dispRate };
  }).filter(Boolean);

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title-group">
          <h1>{t("Reports")}</h1>
          <div className="sub">{t("Spend, payment cycle & supplier performance · MotionReach × Brandstorm consolidated")}</div>
        </div>
      </div>

      <div className="stat-row" style={{gridTemplateColumns: "repeat(5, 1fr)"}}>
        <div className="stat-tile">
          <div className="label">{t('Total spend (vendor master)')}</div>
          <div className="val tnum">{fmtTHB(totalSpend)}</div>
          <div className="delta">{t('across {n} vendors', { n: vList.length })}</div>
        </div>
        <div className="stat-tile">
          <div className="label">{t('Active vendors')}</div>
          <div className="val tnum">{activeCount}</div>
          <div className="delta">{t('of {n} total', { n: vList.length })}</div>
        </div>
        <div className="stat-tile">
          <div className="label">{t('Avg days to pay')}</div>
          <div className="val tnum">{avgDays || '—'}{avgDays ? <span style={{fontSize:18, color:"var(--text-tertiary)"}}>d</span> : ''}</div>
          <div className="delta">{t('across {n} paid invoices', { n: paidInv.length })}</div>
        </div>
        <div className="stat-tile">
          <div className="label">{t('Invoices in flight')}</div>
          <div className="val tnum">{invoices.length}</div>
          <div className="delta">{t('{a} paid · {b} flagged', { a: paidInv.length, b: disputedInv.length })}</div>
        </div>
        <div className="stat-tile">
          <div className="label">{t('Dispute rate')}</div>
          <div className="val tnum">{disputeRate}<span style={{fontSize:18, color:"var(--text-tertiary)"}}>%</span></div>
          <div className="delta">{t('{a} of {b} invoices', { a: disputedInv.length, b: invoices.length })}</div>
        </div>
      </div>

      <div style={{padding: "0 24px 24px", display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16}}>
        <div className="section">
          <div className="section-head">
            <h3>{t('Monthly spend')}</h3>
            <div style={{display: "flex", gap: 8, alignItems: "center"}}>
              <span className="muted" style={{fontSize: 11}}>{t('Trailing 12 months · THB')}</span>
            </div>
          </div>
          <div className="section-body" style={{paddingTop: 8}}>
            {hasMonthly ? <AreaChart data={monthly} height={200}/> : (
              <div style={{height: 200, display:"grid", placeItems:"center", color:"var(--text-tertiary)", fontSize: 13, textAlign:"center", lineHeight:1.5}}>
                {t('No paid invoices yet — monthly spend populates as payments complete.')}
              </div>
            )}
            <div style={{display: "flex", justifyContent: "space-between", marginTop: 12, fontSize: 12}}>
              <div>
                <div className="muted" style={{fontSize: 11}}>{t('This month')}</div>
                <div className="tnum" style={{fontSize: 20, fontWeight: 500, color: "var(--text-accent)"}}>{fmtTHB(thisMonthV)}</div>
              </div>
              <div>
                <div className="muted" style={{fontSize: 11}}>{t('vs prior month')}</div>
                <div className="tnum" style={{fontSize: 14, fontFamily: "var(--font-mono)", color: momPct == null ? "var(--text-tertiary)" : momPct >= 0 ? "var(--success)" : "var(--danger)"}}>
                  {momPct == null ? '—' : `${momPct >= 0 ? '+' : ''}${momPct}%`}
                </div>
              </div>
              <div>
                <div className="muted" style={{fontSize: 11}}>{t('Peak month')}</div>
                <div className="tnum" style={{fontSize: 14, fontFamily: "var(--font-mono)"}}>{peak.v > 0 ? `${peak.l} · ${fmtTHB(peak.v)}` : '—'}</div>
              </div>
              <div>
                <div className="muted" style={{fontSize: 11}}>{t('12-mo total')}</div>
                <div className="tnum" style={{fontSize: 14, fontFamily: "var(--font-mono)"}}>{fmtTHB(total12)}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="section">
          <div className="section-head"><h3>{t('Spend by category')}</h3></div>
          <div className="section-body" style={{display: "flex", gap: 18, alignItems: "center"}}>
            <DonutChart data={byCategory}/>
            <div style={{display: "flex", flexDirection: "column", gap: 8, minWidth: 0, flex: 1}}>
              {byCategory.slice(0, 6).map(d => {
                const total = byCategory.reduce((a, b) => a + b.v, 0);
                return (
                  <div key={d.l} style={{display: "flex", alignItems: "center", gap: 8, fontSize: 12}}>
                    <span style={{width: 8, height: 8, borderRadius: 2, background: d.color, flexShrink: 0}}></span>
                    <span className="ellipsis" style={{flex: 1, minWidth: 0}}>{d.l}</span>
                    <span className="tnum mono muted" style={{fontSize: 11}}>{Math.round(d.v / total * 100)}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="section">
          <div className="section-head"><h3>{t('Top vendors by spend')}</h3></div>
          <div className="section-body" style={{padding: 0}}>
            <table className="tbl" style={{tableLayout: "auto"}}>
              <thead>
                <tr>
                  <th>{t('Vendor')}</th>
                  <th>{t('Category')}</th>
                  <th>{t('Trend')}</th>
                  <th className="right">{t('Spend YTD')}</th>
                  <th className="right">{t('Share')}</th>
                </tr>
              </thead>
              <tbody>
                {topVendors.map((v, i) => {
                  const sparkData = sparkFor(v.vendor_id || v.id);
                  const hasSpark = sparkData.some(x => x > 0);
                  const total = topVendors.reduce((a,b) => a+b.spend, 0);
                  return (
                    <tr key={v.id}>
                      <td>
                        <div style={{display:"flex", alignItems:"center", gap: 10}}>
                          <span style={{
                            width: 22, height: 22, borderRadius: 4,
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            background: "var(--interactive-secondary)",
                            fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-tertiary)",
                          }}>#{i+1}</span>
                          {v.name}
                        </div>
                      </td>
                      <td><CategoryTag id={v.cat}/></td>
                      <td>{hasSpark ? <MiniSpark data={sparkData} color="var(--accent)"/> : <span className="muted" style={{fontSize: 11}}>—</span>}</td>
                      <td className="num right tnum">{fmtTHB(v.spend)}</td>
                      <td className="num right muted">{Math.round(v.spend / total * 100)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="section">
          <div className="section-head"><h3>{t('Payment cycle time')}</h3>
            <span className="muted" style={{fontSize: 11}}>{t('Days from invoice submission to paid')}</span>
          </div>
          <div className="section-body">
            {paidInv.length === 0 ? (
              <div style={{height: 140, display:"grid", placeItems:"center", color:"var(--text-tertiary)", fontSize: 13, textAlign:"center", lineHeight:1.5}}>
                {t('No paid invoices yet — cycle times appear once invoices are paid.')}
              </div>
            ) : (
              <BarChart accentIndex={cycleBars.length - 1} data={cycleBars}/>
            )}
            <div style={{display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginTop: 16, fontSize: 12}}>
              <div>
                <div className="muted" style={{fontSize: 11}}>{t('Fastest stage')}</div>
                <div className="tnum" style={{fontSize: 14, fontFamily: "var(--font-mono)"}}>{fastest ? `${fastest.name} · ${fastest.v}d` : '—'}</div>
              </div>
              <div>
                <div className="muted" style={{fontSize: 11}}>{t('Slowest stage')}</div>
                <div className="tnum" style={{fontSize: 14, fontFamily: "var(--font-mono)", color: "var(--warning)"}}>{slowest ? `${slowest.name} · ${slowest.v}d` : '—'}</div>
              </div>
              <div>
                <div className="muted" style={{fontSize: 11}}>{t('Avg total')}</div>
                <div className="tnum" style={{fontSize: 14, fontFamily: "var(--font-mono)"}}>{avgDays ? `${avgDays}d` : '—'}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="section" style={{gridColumn: "1 / -1"}}>
          <div className="section-head"><h3>{t('Performance — by category')}</h3></div>
          <table className="tbl" style={{tableLayout: "auto"}}>
            <thead>
              <tr>
                <th>{t('Category')}</th>
                <th className="right">{t('Vendors')}</th>
                <th className="right">{t('Spend')}</th>
                <th className="right">{t('Avg quality')}</th>
                <th className="right">{t('On-time %')}</th>
                <th className="right">{t('Avg cycle (d)')}</th>
                <th className="right">{t('Invoices')}</th>
                <th className="right">{t('Dispute rate')}</th>
              </tr>
            </thead>
            <tbody>
              {catRows.map(({ c, vendors, spend, avgQ, onTimePct, avgCyc, invoices: invN, dispRate }) => (
                <tr key={c.id}>
                  <td><CategoryTag id={c.id}/></td>
                  <td className="num right tnum">{vendors}</td>
                  <td className="num right tnum">{spend ? fmtTHB(spend) : "—"}</td>
                  <td className="right tnum mono">{avgQ == null ? "—" : avgQ.toFixed(1)}</td>
                  <td className="right tnum mono" style={{color: onTimePct != null && onTimePct >= 90 ? "var(--success)" : "var(--text-secondary)"}}>{onTimePct == null ? "—" : `${onTimePct}%`}</td>
                  <td className="right tnum mono">{avgCyc == null ? "—" : avgCyc}</td>
                  <td className="num right tnum muted">{invN || "—"}</td>
                  <td className="right tnum mono" style={{color: dispRate != null && dispRate > 3 ? "var(--warning)" : "var(--text-secondary)"}}>{dispRate == null ? "—" : `${dispRate}%`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

